import { Box, List, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { getTaxInfo } from '@/api/tax-info';

import { StatusProgressDots } from '@/components/DataDisplay/Indicator/StatusProgressDots';
import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';
import { ReviewAndSubmitSection } from '@/components/PageForm/MoreInfoForm/ReviewAndSubmit/ReviewAndSubmitSection';
import { TermsAndConditionsDrawer } from '@/components/PageForm/TermsAndConditions/TermsAndConditionsDrawer';

import { DOCUMENT_COMPLETED_STATUSES } from '@/constants/document';

import { useDrawerOpenTracker } from '@/context';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useEligibleChangedPopup from '@/hooks/useEligibleChangedPopup';
import { useTranslate } from '@/hooks/useTranslate';
import { useWindowFocus } from '@/hooks/useWindowFocus';

import { notifyUserChanged } from '@/redux/auth/actions';
import { selectIsIdentityChecked } from '@/redux/auth/selectors';
import {
  selectAdobeSignFormFeature,
  selectConfigsState,
} from '@/redux/configs';
import { selectTerritories } from '@/redux/countries';
import {
  selectBasicInfoStatus,
  selectSigningDocuments,
  selectUserData,
} from '@/redux/user-data/selectors';

import { Tenant } from '@/types/common';
import { DocumentSection, DocumentStatus } from '@/types/document';
import { UserStatus } from '@/types/user';

import { openICBLinkByPlatform } from '@/utils/browser';
import { compact } from '@/utils/collection';
import { isNil } from '@/utils/common';
import { getIdentityDocumentCheckStatus } from '@/utils/document';
import {
  isAdultUser,
  isCaymanUser,
  isExistingCustomer,
  isGuyanaExcWithIdOnly,
  isIndividualUser,
  isRemediationCustomer,
  isRightStartUser,
  shouldShowBranchVisitPopup,
  shouldShowExcBranchVisitPopup,
} from '@/utils/user';

import { DocumentSigning } from './components/DocumentSigning';
import { IdentityCheck } from './components/IdentityCheck';
import { PersonalDetails } from './components/PersonalDetails/PersonalDetails';
import { WarrantyOfAccuracyAccordion } from './components/WarrantyOfAccuracyAccordion';
import { ApprovedDrawer } from './drawers/ApprovedDrawer';
import { BranchSelectionDrawer } from './drawers/BranchSelectionDrawer';
import { QuestionsDrawer } from './drawers/QuestionsDrawer/QuestionsDrawer';
import { RemediationThankYouDrawer } from './drawers/RemediationThankYouDrawer';
import { SuccessSubmissionDrawer } from './drawers/SuccessSubmissionDrawer';
import { isTaxInfoComplete } from './drawers/TaxInfoDrawer/utils/taxInfoStatus';
import { AdditionalID } from './RequiredDocuments/AdditionalID';
import { ProofOfAddress } from './RequiredDocuments/ProofOfAddress';
import ProofOfEmployment from './RequiredDocuments/ProofOfEmployment';
import { ProofOfRelationship } from './RequiredDocuments/ProofOfRelationship';
import { TaxInfo } from './RequiredDocuments/TaxInfo';
import { TIN } from './RequiredDocuments/TIN';
import { USCitizenshipRenouncement } from './RequiredDocuments/USCitizenshipRenouncement';
import { YourFinances } from './YourFinances';
import { useYourFinancesStatus } from './YourFinances/hooks';

import { ApprovedImg } from './drawers/Drawers.styled';
import { CaymanLogo, MoreInfoFormContainer } from './MoreInfoForm.styled';

export const enum MoreInfoSection {
  BasicInfo,
  DocumentSigning,
  BranchSelection,
  UpdatePersonalInfo,
  ProofOfAddress,
}

export function MoreInformationForm() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const [drawer, setDrawer] = useState<MoreInfoSection>();

  const { value: isApprovedDismissed, setTrue: dismissApproved } =
    useBoolean(false);
  const {
    value: isRemediationApprovedDismissed,
    setTrue: dismissRemediationApproved,
  } = useBoolean(false);
  const { value: isPreApprovedDismissed, setTrue: dismissPreApproved } =
    useBoolean(false);
  const { value: isIneligibleDismissed, setTrue: dismissIneligible } =
    useBoolean(false);
  const { value: isSubmittedDismissed, setTrue: dismissSubmitted } =
    useBoolean(false);
  const {
    value: isSuccessSubmissionDismissed,
    setTrue: dismissSuccessSubmission,
    setFalse: resetSuccessSubmissionDismissal,
  } = useBoolean(false);
  const {
    value: isBranchVisitPopupDismissed,
    setTrue: dismissBranchVisitPopup,
  } = useBoolean(false);
  const {
    value: isBranchVisitPopupDismissedForNewCustomer,
    setTrue: dismissBranchVisitPopupForNewCustomer,
  } = useBoolean(false);
  const { requiredDocumentSections, user } = useAppSelector(selectUserData);
  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const { isBranchSelectionEnabled, links } =
    useAppSelector(selectConfigsState);
  const { documentsSigningStatus, remainingDocsToSign } = useAppSelector(
    selectSigningDocuments,
  );
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const isFormRemovalEnabled =
    !isAdobeSignFormEnabled && !isRightStartUser(user);
  const isIdentityChecked = useAppSelector(selectIsIdentityChecked);
  const {
    isVisible: isYourFinancesVisible,
    isComplete: isYourFinancesComplete,
    isLoading: isYourFinancesLoading,
  } = useYourFinancesStatus();
  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
    enabled: isFormRemovalEnabled,
  });

  const existingCustomer = isExistingCustomer(user);

  // When one BasicInfo, POA, POE is not completed, disable the document signing
  const isStatusCompleted = (status: DocumentStatus | undefined) =>
    status === DocumentStatus.InReview || status === DocumentStatus.Approved;

  const isStatusApproved = (status: DocumentStatus | undefined) =>
    status === DocumentStatus.Approved;

  const areAllDocumentsApproved = useMemo(() => {
    const areProofDocumentsApproved = requiredDocumentSections.every(section =>
      isStatusApproved(section.status),
    );

    return (
      isStatusApproved(basicInfoStatus) &&
      areProofDocumentsApproved &&
      isStatusApproved(documentsSigningStatus)
    );
  }, [basicInfoStatus, documentsSigningStatus, requiredDocumentSections]);

  const isIdentityMissing = useMemo(() => {
    if (!user) return true;
    if (isExistingCustomer(user)) return !isIdentityChecked;
    if (isRemediationCustomer(user)) {
      return user.requireIdentity && !isIdentityChecked;
    }
    return false;
  }, [user, isIdentityChecked]);

  // When one BasicInfo, POA, POE is not completed, disable the document signing
  const isDocumentSigningDisabled = useMemo(() => {
    const proofOfAddressSection = requiredDocumentSections.find(
      section => section.type === DocumentSection.ProofOfAddress,
    );

    const proofOfEmploymentSection = requiredDocumentSections.find(
      section => section.type === DocumentSection.ProofOfEmployment,
    );
    const secondIdSection = requiredDocumentSections.find(
      section => section.type === DocumentSection.SecondID,
    );
    const usRenouncementSection = requiredDocumentSections.find(
      section => section.type === DocumentSection.USCitizenshipRenouncement,
    );
    const taxSupportingDocumentSection = requiredDocumentSections.find(
      section => section.type === DocumentSection.TaxSupportingDocument,
    );

    // If POA is not completed or still pending, disable the document signing
    const isDisabledPOA =
      proofOfAddressSection?.status === DocumentStatus.Pending;

    // If POE is not completed or still pending, disable the document signing
    const isDisabledPOE =
      proofOfEmploymentSection?.status === DocumentStatus.Pending;

    // If second is present
    const isDisabledSecondId =
      secondIdSection?.status === DocumentStatus.Pending;

    const isDisabledUSRenouncement =
      usRenouncementSection?.status === DocumentStatus.Pending;

    const isDisabledByYourFinances =
      isYourFinancesLoading ||
      (isYourFinancesVisible && !isYourFinancesComplete);

    const isDisabledByTaxInfo =
      isFormRemovalEnabled && !isTaxInfoComplete(taxInfo);

    const isDisabledByTaxSupportingDocument =
      !!taxSupportingDocumentSection &&
      taxSupportingDocumentSection.status === DocumentStatus.Pending;

    return (
      basicInfoStatus === undefined ||
      isDisabledPOA ||
      isDisabledPOE ||
      isDisabledSecondId ||
      isDisabledUSRenouncement ||
      isIdentityMissing ||
      isDisabledByYourFinances ||
      isDisabledByTaxInfo ||
      isDisabledByTaxSupportingDocument
    );
  }, [
    requiredDocumentSections,
    basicInfoStatus,
    isIdentityMissing,
    isYourFinancesVisible,
    isYourFinancesComplete,
    isFormRemovalEnabled,
    taxInfo,
    isYourFinancesLoading,
  ]);

  const isCompleted = useMemo(() => {
    const isBasicInfoCompleted = isStatusCompleted(basicInfoStatus);

    const areProofDocumentsCompleted = requiredDocumentSections.every(section =>
      isStatusCompleted(section.status),
    );

    const isDocumentsSigningCompleted =
      isFormRemovalEnabled ||
      (DOCUMENT_COMPLETED_STATUSES.includes(documentsSigningStatus) &&
        remainingDocsToSign === 0);

    const isYourFinancesCompleted =
      !isYourFinancesLoading &&
      (!isYourFinancesVisible || isYourFinancesComplete);

    const isTaxInfoCompleted =
      !isFormRemovalEnabled || isTaxInfoComplete(taxInfo);

    return (
      isBasicInfoCompleted &&
      !isIdentityMissing &&
      areProofDocumentsCompleted &&
      isDocumentsSigningCompleted &&
      isYourFinancesCompleted &&
      isTaxInfoCompleted
    );
  }, [
    basicInfoStatus,
    isIdentityMissing,
    documentsSigningStatus,
    requiredDocumentSections,
    isYourFinancesVisible,
    isYourFinancesComplete,
    isYourFinancesLoading,
    remainingDocsToSign,
    isFormRemovalEnabled,
    taxInfo,
  ]);

  const selectedBranchName = useMemo(() => {
    if (user) {
      return (
        territoryByCountryCode[
          String(user.tenant).toUpperCase() as Tenant
        ]?.branches.find(branch => branch.id === user.visitingBranchId)
          ?.branchName || ''
      );
    }
    return '';
  }, [user, territoryByCountryCode]);

  useEffect(() => {
    window.focus();
  }, []);

  useEffect(() => {
    if (!user?.infoAccuracyConfirmed) {
      resetSuccessSubmissionDismissal();
    }
  }, [resetSuccessSubmissionDismissal, user?.infoAccuracyConfirmed]);

  useWindowFocus(() => {
    dispatch(notifyUserChanged());
  });

  const openDrawer = (drawer: MoreInfoSection) => () => {
    setDrawer(drawer);
  };

  const hideDrawer = () => setDrawer(undefined);

  useEligibleChangedPopup(
    requiredDocumentSections,
    user,
    openDrawer(MoreInfoSection.ProofOfAddress),
  );

  const handleGoToMyAccount = useCallback(() => {
    dismissRemediationApproved();
    openICBLinkByPlatform(links);
  }, [dismissRemediationApproved, links]);
  const renderInfoDrawers = () => {
    if (!user) return null;
    if (!user?.termsAndConditions && !isRemediationCustomer(user)) {
      return <TermsAndConditionsDrawer open />;
    }

    if (
      isCompleted &&
      !isNil(user?.infoAccuracyConfirmed) &&
      isCaymanUser(user) &&
      !isSubmittedDismissed
    ) {
      return (
        <ConfirmDrawer
          description={t('moreInfo.form.submittedDrawer.description')}
          drawerImage={
            <CaymanLogo
              src="/cayman-logo-with-text.svg"
              alt="Cayman National"
            />
          }
          primaryText={t('moreInfo.form.submittedDrawer.button.gotIt')}
          showHeader={false}
          primaryAction={dismissSubmitted}
          onClose={dismissSubmitted}
        />
      );
    }

    if (
      user.status === UserStatus.PreApproved &&
      !isPreApprovedDismissed &&
      !isCaymanUser(user) &&
      !isExistingCustomer(user)
    ) {
      const preApprovedKey =
        user.tenant === Tenant.Guyana
          ? 'moreInfo.preApprovedDrawerVerifyID'
          : 'moreInfo.preApprovedDrawer';

      return (
        <ConfirmDrawer
          title={t(`${preApprovedKey}.title`)}
          description={t(`${preApprovedKey}.body`)}
          icon="info-outline"
          iconColor={theme => theme.palette.primaries[400]}
          primaryAction={dismissPreApproved}
          primaryText={t(`${preApprovedKey}.button.dismiss`)}
          onClose={dismissPreApproved}
        />
      );
    }

    if (
      user.status === UserStatus.PreApproved &&
      !isPreApprovedDismissed &&
      !isCaymanUser(user) &&
      isExistingCustomer(user) &&
      !isGuyanaExcWithIdOnly(user)
    ) {
      return (
        <ConfirmDrawer
          title={t('moreInfo.preApprovedDrawer.title')}
          description={t('moreInfo.preApprovedDrawer.body')}
          icon="info-outline"
          iconColor={theme => theme.palette.primaries[400]}
          primaryAction={dismissPreApproved}
          primaryText={t('moreInfo.preApprovedDrawer.button.dismiss')}
          onClose={dismissPreApproved}
        />
      );
    }

    if (
      isRemediationCustomer(user) &&
      areAllDocumentsApproved &&
      (user.status === UserStatus.Approved ||
        user.status === UserStatus.Opened) &&
      (!user.requireIdentity || isIdentityChecked) &&
      !isRemediationApprovedDismissed
    ) {
      return (
        <ConfirmDrawer
          drawerImage={<ApprovedImg />}
          title={t('moreInfo.remediationApprovedDrawer.title')}
          description={t('moreInfo.remediationApprovedDrawer.body')}
          primaryAction={handleGoToMyAccount}
          primaryText={t(
            'moreInfo.remediationApprovedDrawer.button.goToAccount',
          )}
          onClose={dismissRemediationApproved}
        />
      );
    }

    if (
      isRemediationCustomer(user) &&
      user.infoAccuracyConfirmed &&
      user.status === UserStatus.PendingApproval
    ) {
      return <RemediationThankYouDrawer />;
    }

    if (
      !isAdobeSignFormEnabled &&
      isIndividualUser(user) &&
      !isRemediationCustomer(user) &&
      user.infoAccuracyConfirmed &&
      user.eligible &&
      !isSuccessSubmissionDismissed
    ) {
      return <SuccessSubmissionDrawer onClose={dismissSuccessSubmission} />;
    }

    if (
      (user.status === UserStatus.Approved ||
        user.status === UserStatus.Opened) &&
      !isApprovedDismissed &&
      !isRemediationCustomer(user)
    ) {
      return <ApprovedDrawer tenant={user?.tenant} onClose={dismissApproved} />;
    }

    if (
      isCompleted &&
      user?.eligible === false &&
      !isNil(user?.infoAccuracyConfirmed) &&
      !isIneligibleDismissed &&
      !isCaymanUser(user)
    ) {
      return (
        <ConfirmDrawer
          title={t('moreInfo.form.confirmDrawer.title')}
          description={t('moreInfo.form.confirmDrawer.description')}
          icon="info"
          primaryAction={dismissIneligible}
          primaryText={t('moreInfo.form.confirmDrawer.primaryText')}
          onClose={dismissIneligible}
        />
      );
    }

    if (
      isCompleted &&
      isExistingCustomer(user) &&
      !isCaymanUser(user) &&
      !isBranchVisitPopupDismissed &&
      shouldShowExcBranchVisitPopup(user)
    ) {
      return (
        <ConfirmDrawer
          title={t('moreInfo.form.branchVisitPopup.title')}
          description={t('moreInfo.form.branchVisitPopup.description')}
          icon="info-outline"
          iconColor={theme => theme.palette.primaries[400]}
          primaryAction={dismissBranchVisitPopup}
          primaryText={t('moreInfo.form.branchVisitPopup.button.dismiss')}
          onClose={dismissBranchVisitPopup}
        />
      );
    }

    if (
      isCompleted &&
      !isExistingCustomer(user) &&
      !isCaymanUser(user) &&
      !isBranchVisitPopupDismissedForNewCustomer &&
      shouldShowBranchVisitPopup(user)
    ) {
      return (
        <ConfirmDrawer
          title={t('moreInfo.form.branchVisitPopup.title')}
          description={t('moreInfo.form.branchVisitPopup.description')}
          icon="info-outline"
          iconColor={theme => theme.palette.primaries[400]}
          primaryAction={dismissBranchVisitPopupForNewCustomer}
          primaryText={t('moreInfo.form.branchVisitPopup.button.dismiss')}
          onClose={dismissBranchVisitPopupForNewCustomer}
        />
      );
    }
  };

  // TermsAndConditionsDrawer is out of scope for the drawer-open tracker (it
  // lives on a different page-step and can't co-occur with this form), so it
  // must be excluded here. This condition mirrors renderInfoDrawers()'s first
  // `if` by construction - keep both in sync if that condition changes.
  const isTermsAndConditionsDrawerOpen =
    !!user && !user.termsAndConditions && !isRemediationCustomer(user);
  const infoDrawer = renderInfoDrawers();

  useDrawerOpenTracker(
    drawer === MoreInfoSection.BasicInfo ||
      drawer === MoreInfoSection.BranchSelection ||
      (!!infoDrawer && !isTermsAndConditionsDrawerOpen),
  );

  const isBasicInfoDisabled = useMemo(() => {
    if (user && isExistingCustomer(user)) {
      return !user.clientReviewed;
    }
    return basicInfoStatus === DocumentStatus.Rejected;
  }, [basicInfoStatus, user]);

  const onBranchSelectionClick = () => {
    setDrawer(MoreInfoSection.BranchSelection);
  };

  const isAdultJointAccount = isAdultUser(user);

  const personalDetailsStatus = user?.clientReviewed
    ? DocumentStatus.Approved
    : DocumentStatus.Pending;

  const adultStatusDots = useMemo(() => {
    if (!isAdultJointAccount) return [];
    return compact([
      ...(existingCustomer ? [personalDetailsStatus] : []),
      basicInfoStatus ?? DocumentStatus.Pending,
      ...(existingCustomer
        ? [getIdentityDocumentCheckStatus(user?.documents, 3)]
        : []),
      ...requiredDocumentSections.map(({ status }) => status),
      documentsSigningStatus,
    ]);
  }, [
    personalDetailsStatus,
    basicInfoStatus,
    user?.documents,
    requiredDocumentSections,
    documentsSigningStatus,
    isAdultJointAccount,
    existingCustomer,
  ]);

  if (!user) return null;

  return (
    <>
      {isAdultJointAccount && (
        <Stack
          justifyContent="center"
          direction={'column'}
          width={'100%'}
          sx={{ mt: 2 }}
        >
          <StatusProgressDots dots={adultStatusDots} />
        </Stack>
      )}
      <MoreInfoFormContainer>
        <List>
          {/* Personal Details Section */}
          {isExistingCustomer(user) && <PersonalDetails />}

          {/* Basic Information Section */}
          <DocumentActionItem
            title={t('moreInfo.form.basicInfo.title')}
            description={t('moreInfo.form.basicInfo.description')}
            onClick={openDrawer(MoreInfoSection.BasicInfo)}
            status={basicInfoStatus}
            disabled={isBasicInfoDisabled}
          />

          {/* Identity Check Section */}
          {(isExistingCustomer(user) ||
            (isRemediationCustomer(user) && user?.requireIdentity)) && (
            <IdentityCheck />
          )}

          <TIN />

          <USCitizenshipRenouncement />

          <ProofOfRelationship />

          <ProofOfAddress
            open={drawer === MoreInfoSection.ProofOfAddress}
            onOpen={openDrawer(MoreInfoSection.ProofOfAddress)}
            onClose={hideDrawer}
          />

          <ProofOfEmployment />

          <AdditionalID />

          {isFormRemovalEnabled && <TaxInfo />}

          <YourFinances />

          {isBranchSelectionEnabled && (
            <DocumentActionItem
              title={t('moreInfo.form.branchSelection.title')}
              richDescription={
                <Trans
                  i18nKey="moreInfo.form.branchSelection.description"
                  values={{ branch: selectedBranchName }}
                  components={{
                    b: <Typography variant="captionBold" component="span" />,
                  }}
                />
              }
              onClick={onBranchSelectionClick}
              status={DocumentStatus.DefaultApproved}
              enabledForAllStatuses
            />
          )}
          {/* Document Signing Section */}
          {!isFormRemovalEnabled &&
            (!isRemediationCustomer(user) || user.isHighRisk) && (
              <DocumentSigning disabled={isDocumentSigningDisabled} />
            )}

          <Box mt={1.5}>
            {!isFormRemovalEnabled ? (
              <WarrantyOfAccuracyAccordion disabled={!isCompleted} />
            ) : (
              <ReviewAndSubmitSection disabled={!isCompleted} />
            )}
          </Box>
        </List>
        {user && (
          <QuestionsDrawer
            open={drawer === MoreInfoSection.BasicInfo}
            onClose={hideDrawer}
            user={user}
          />
        )}
        <BranchSelectionDrawer
          open={drawer === MoreInfoSection.BranchSelection}
          onClose={hideDrawer}
          user={user}
        />
        {infoDrawer}
      </MoreInfoFormContainer>
    </>
  );
}
